import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React from "react";

export type DSButtonVariant =
  | "primary"
  | "secondary"
  | "cancel"
  | "danger";
export type DSButtonSize = "sm" | "md" | "lg";

export interface DSButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: DSButtonVariant;
  size?: DSButtonSize;
  /** Optional leading icon (FontAwesome IconProp) */
  icon?: IconProp;
  children: React.ReactNode;
  dataTestId: string;
  ref?: React.Ref<HTMLButtonElement>;
}

export const DSButton = ({
  variant = "primary",
  size = "md",
  icon,
  disabled = false,
  children,
  dataTestId,
  ref,
  ...props
}: Omit<DSButtonProps, "className">) => {
  // Base styles
  const baseStyles =
    "relative inline-flex items-center justify-center rounded-full font-medium transition-colors whitespace-nowrap";

  // Variant styles
  const variantStyles = {
    primary: disabled
      ? "bg-disabled opacity-80 text-white shadow-sm cursor-not-allowed"
      : "bg-brand text-white shadow-sm hover:bg-indigo-600",
    secondary: disabled
      ? "border border-disabled opacity-80 text-placeholder shadow-sm cursor-not-allowed"
      : "border border-brand text-brand shadow-sm hover:bg-indigo-100 dark:hover:bg-indigo-950",
    cancel: disabled
      ? "bg-tertiary opacity-80 text-placeholder cursor-not-allowed"
      : "bg-tertiary text-secondary hover:text-primary hover:bg-grindigo-200 dark:hover:bg-grindigo-900",
    danger: disabled
      ? "bg-disabled opacity-80 text-white shadow-sm cursor-not-allowed"
      : "bg-danger text-white shadow-sm hover:bg-red-600",
  };

  // Size styles
  const sizeStyles = {
    sm: icon ? "px-md py-xs gap-sm text-sm" : "px-md py-xs text-sm",
    md: icon
      ? "px-md py-xs gap-sm text-base"
      : "px-md py-xs text-base",
    lg: icon ? "px-lg py-sm gap-sm text-lg" : "px-lg py-sm text-lg",
  };

  return (
    <button
      ref={ref}
      disabled={disabled}
      data-testid={dataTestId}
      data-intercom-target={dataTestId}
      className={`${baseStyles} ${variantStyles[variant]} ${sizeStyles[size]}`}
      {...props}
    >
      {icon && <FontAwesomeIcon icon={icon} />}
      {children}
    </button>
  );
};

DSButton.displayName = "DSButton";
